import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";

import UserProfileModal from "../../screens/user/userProfile/UserProfileModal";
import { CommonContainer } from "../commonContainer/commonContainer";
import { ImageView } from "../ImageView/ImageView";
import ModalMenuBar from "../menuBar/ModalMenu";
import { HeaderMobileView } from "./HeaderMobileView";
import { HeaderWebView } from "./HeaderWebView";

import { useAuth } from "react-oidc-context";
import { useGetCompanySearch, useGetCompanyToken } from "../../api/company";
import { useGetNotification, useNotificationRead } from "../../api/notification";
import { usePostUserLogout } from "../../api/userDetails";
import { images } from "../../assets/images";
import defaultUserImage from "../../assets/images/default_profile_pic.jpeg";
import { getHeaderMenuItems } from "../../helpers/AuthorizationHelper";
import { errorView } from "../../helpers/ErrorHandler";
import { nameSort } from "../../helpers/NameSort";
import { useAuthContext } from "../../hooks/useAuthContext";
import { userRoles } from "../../routes/useRoleAccess";
import {
  getUserIdInLocal,
  saveCompanyTenantId,
  saveCompanyTokenInLocal,
  saveRefreshTokenInLocal,
  saveTenantId,
  saveTokenExpiration
} from "../../utils/cacheStorage";

import { DISABLE_COGNITO_LOGIN } from "../../config/constants";
import { useCompanyContext } from "../../context/CompanyContext";
import "./Header.scss";

export const Header = memo(({ userDetails, setVisible, isLogin }: any) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { refreshCompanyList } = useCompanyContext();
  const { user, newNotification, dispatch, logout }: any = useAuthContext();

  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleUserProfile, setVisibleUserProfile] = useState(false);
  const [loadMore, setLoadMore] = useState(0);

  const baseOptions = useMemo(() => getHeaderMenuItems(navigate), [navigate]);
  const additionalOptions = useMemo(
    () => [
      {
        key: "profile",
        text: "Profile",
        value: "profile",
        onClick: () => setVisibleUserProfile(true)
      },
      { key: "logout", text: "Logout", value: "logout", onClick: () => userLogout() }
    ],
    []
  );
  const options = useMemo(
    () => [...baseOptions, ...additionalOptions],
    [baseOptions, additionalOptions]
  );

  const signOutRedirect = () => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = process.env.REACT_APP_COGNITO_LOG_OUT_URI;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    auth.removeUser();
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  const { isLoading: loadLogoutUser, mutate: userLogOut } = usePostUserLogout();
  const { mutate: updateIsReadNotification } = useNotificationRead();
  const {
    data: company,
    isLoading: loadCompanyData,
    mutate: searchCompany
  } = useGetCompanySearch();

  const {
    data: tokenData,
    isLoading: loadCompanyToken,
    mutate: getCompanyToken,
    isSuccess: isCompanyTokenSuccess
  } = useGetCompanyToken();

  const {
    isLoading: isLoadingNotification,
    data: notificationData,
    refetch
  } = useGetNotification(user, newNotification, loadMore);

  // Fetch company list initially
  useEffect(() => {
    if (localStorage.getItem("IdToken")) {
      searchCompany("");
    }
  }, [searchCompany, refreshCompanyList]);

  // Save company token and reload
  useEffect(() => {
    if (isCompanyTokenSuccess) {
      if (tokenData?.token) {
        saveCompanyTokenInLocal(tokenData.token);
        saveRefreshTokenInLocal(tokenData.refreshToken);
        saveTokenExpiration(tokenData.expiredIn);
        window.location.reload();
      } else {
        errorView("Company loading issue");
      }
    }
  }, [isCompanyTokenSuccess, tokenData]);

  const getSearchValue = useCallback(
    (data: any) => {
      const tenantId = data?._id;
      const title = data?.name;
      const companyTenantId = data?.tenantId;
      if (localStorage.getItem("IdToken")) {
        saveTenantId(companyTenantId);
        saveCompanyTenantId(companyTenantId);
        getCompanyToken(tenantId);
        dispatch({ type: "SET_COMPANY", payload: title });
        dispatch({ type: "SET_COMPANY_ID", payload: tenantId });
        navigate("/");
      }
    },
    [dispatch, getCompanyToken, navigate]
  );

  const logoutUserWithCognito = () => {
    if (DISABLE_COGNITO_LOGIN) return navigate("/auth");
    signOutRedirect();
  };

  const handleUserLogout = () => {
    logout();
    logoutUserWithCognito();
  };

  const userLogout = useCallback(() => {
    userLogOut("", {
      onSuccess: () => handleUserLogout(),
      onError: () => {
        handleUserLogout();
        errorView("User logout fail");
      }
    });
  }, [dispatch, navigate, userLogOut]);

  const onClickNotification = useCallback(
    (id: string) => {
      updateIsReadNotification(id, { onSuccess: () => refetch });
    },
    [updateIsReadNotification, refetch]
  );

  const renderUserImage = useMemo(() => {
    if (isLogin) return null;

    const profileImg = user?.profileUrl || defaultUserImage;

    return (
      <>
        <label className="userName">Hi, {nameSort(user?.firstName || "")}</label>
        {[profileImg, images?.DropDownIcon].map((src, idx) => (
          <ImageView
            key={idx}
            avatar
            imgOne={src}
            imgTwo={src}
            addRandomId={false}
            customImageView="userProfileTopHeader"
          />
        ))}
      </>
    );
  }, [isLogin, user]);

  if (loadCompanyToken || loadLogoutUser) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <HeaderWebView
        options={options}
        userImage={renderUserImage}
        navigate={navigate}
        userDetails={userDetails}
        company={company}
        getSearchValue={getSearchValue}
        loadCompanyData={loadCompanyData}
        loadCompanyToken={loadCompanyToken}
        searchCompany={searchCompany}
        isLoadingNotification={isLoadingNotification}
        notificationData={notificationData}
        updateLoadMore={(isListEmpty: boolean) => {
          setLoadMore(isListEmpty ? 0 : loadMore + 10);
        }}
        onClickNotification={onClickNotification}
      />
      <CommonContainer>
        <HeaderMobileView
          options={options}
          userImage={renderUserImage}
          navigate={navigate}
          userDetails={userDetails}
          setVisible={setVisible}
          modalStatue={() => setVisibleModal(!visibleModal)}
          company={company}
          getSearchValue={getSearchValue}
          loadCompanyData={loadCompanyData}
          loadCompanyToken={loadCompanyToken}
          searchCompany={searchCompany}
        />
      </CommonContainer>
      <ModalMenuBar visibleModal={visibleModal} setVisibleModal={() => setVisibleModal(false)} />
      {!isLogin && (
        <UserProfileModal
          visibleModal={visibleUserProfile}
          setVisibleModal={() => setVisibleUserProfile(false)}
          id={getUserIdInLocal()}
          userRolesData={userRoles}
        />
      )}
    </>
  );
});
